import React, { useEffect, useState } from 'react';

import { INLINES } from '@contentful/rich-text-types';
import CheckIcon from '@shared/assets/svgs/checkIcon.svg';
import ToastClose from '@shared/assets/svgs/toastClose.svg';
import { Link } from 'gatsby';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import Confetti from 'react-dom-confetti';
import { Slide } from 'react-toastify';

import { useToastCmsData } from '../hooks/useToastCmsData';

// The Toast component uses Toastify to display notifications to the user in 3 ways:
// When the user logs into the site, if there is a site wide message to display (custom)
// When the user logs into the site, and has started the FUO but has not yet completed it (onboarding)
// When the user completes a task in a checklist (celebratory)
interface ToastOptions {
  toastId: string;
  transition: typeof Slide;
  hideProgressBar: boolean;
  position: 'bottom-left';
  className: string;
}

type ToastStyle = 'celebratory' | 'onboarding' | 'custom' | 'default';

const richTextOptions = {
  renderNode: {
    [INLINES.HYPERLINK]: (node: any, children: React.ReactNode) => {
      return (
        <a
          href={node.data.uri}
          className="text-core-neutral-white font-serif underline"
          target="_blank"
        >
          {children}
        </a>
      );
    },
    [INLINES.ENTRY_HYPERLINK]: (node: any, children: React.ReactNode) => {
      return (
        <Link
          to={node.data.target.slug}
          target="_blank"
          className="text-core-neutral-white font-serif underline"
        >
          {children}
        </Link>
      );
    },
    [INLINES.ASSET_HYPERLINK]: (node: any, children: React.ReactNode) => {
      return (
        <a
          href={node.data.target.file.url}
          className="text-core-neutral-white font-serif underline"
          target="_blank"
        >
          {children}
        </a>
      );
    },
  },
};

export const toastOptions = (toastStyle: ToastStyle): ToastOptions => {
  switch (toastStyle) {
    case 'celebratory':
      return {
        toastId: 'celebratory',
        transition: Slide,
        hideProgressBar: true,
        position: 'bottom-left',
        className:
          'bg-freestyle-accent-2 px-[24px] py-[24px] rounded-[16px]  w-full md:w-[418px]  shadow-[0px_10px_10px_-5px_rgba(0,0,0,0.10)]',
      };
    case 'onboarding':
      return {
        toastId: 'onboarding',
        transition: Slide,
        hideProgressBar: true,
        position: 'bottom-left',
        className:
          'bg-core-accent-3 px-[24px] py-[24px] rounded-[16px] w-full md:w-[418px] shadow-[0px_10px_10px_-5px_rgba(0,0,0,0.10)]',
      };
    case 'custom':
      return {
        toastId: 'custom',
        transition: Slide,
        hideProgressBar: true,
        position: 'bottom-left',
        className:
          'bg-core-accent-3 px-[24px] py-[24px] rounded-[16px] w-full md:w-[418px]  shadow-[0px_10px_10px_-5px_rgba(0,0,0,0.10)]',
      };
    default:
      return {
        toastId: 'default',
        transition: Slide,
        hideProgressBar: true,
        position: 'bottom-left',
        className:
          'bg-neutral-black px-[24px] py-[24px] rounded-[16px]   w-full md:w-[418px]  shadow-[0px_10px_10px_-5px_rgba(0,0,0,0.10)]',
      };
  }
};

// Config for confetti styles
const confettiConfig = {
  angle: '90',
  spread: '190',
  startVelocity: '21',
  elementCount: '52',
  dragFriction: 0.12,
  duration: 5000,
  stagger: 3,
  width: '10px',
  height: '10px',
  perspective: '500px',
  colors: ['#a864fd', '#29cdff', '#78ff44', '#ff718d', '#fdff6a'],
};

// define the custom toast inner component with styles
interface CustomToastProps {
  closeToast?: () => void;
  toastType: string;
  userName: string;
}

export const CustomToast = ({
  closeToast,
  toastType,
  userName,
}: CustomToastProps) => {
  // Get users name for text replacement
  // const { profile, loading, error, isSSO } = useProfile();
  // const getUserName = () => {
  //   if (!isSSO) return 'Test User';
  //   if (loading) return '--';
  //   if (error || !profile) return 'Test User';
  //   return `${profile.givenName}`;
  // };
  // const userFirstName = getUserName();
  // const [userName, setUserName] = useState(userFirstName);

  // useEffect(() => {
  //   setUserName(profile?.givenName || 'Test User');
  // }, [profile?.givenName]);

  // Get CMS data
  const cmsData = useToastCmsData() as Queries.allStrapiToastContainersQuery['allStrapiToastContainer']['nodes'];

  // Format CMS data with user name
  // Celebratory toast
  const celebratoryCmsData = cmsData.find(
    toast => toast.toastType === 'celebratory',
  );
  const celebratoryTitle = celebratoryCmsData?.toastContent?.title?.replace(
    /{userName}/gi,
    userName,
  );
  const celebratoryBodyContent = celebratoryCmsData?.toastContent?.body?.internal?.content;
  const celebratoryBodyString = celebratoryBodyContent 
    ? celebratoryBodyContent.replace(/{userName}/gi, userName)
    : null;
  const celebratoryBody = celebratoryBodyString ? JSON.parse(celebratoryBodyString) : null;
  // Onboarding toast
  const onboardingCmsData = cmsData.find(
    toast => toast.toastType === 'onboarding',
  );
  const onboardingTitle = onboardingCmsData?.toastContent?.title?.replace(
    /{userName}/gi,
    userName,
  );
  const onboardingBodyContent = onboardingCmsData?.toastContent?.body?.internal?.content;
  const onboardingBodyString = onboardingBodyContent
    ? onboardingBodyContent.replace(/{userName}/gi, userName)
    : null;
  const onboardingBody = onboardingBodyString ? JSON.parse(onboardingBodyString) : null;
  // Custom toast (if it exists)
  const customCmsData = cmsData.find(toast => toast.toastType === 'custom');
  let customTitle: string | undefined;
  let customBody: any = null;

  if (customCmsData && customCmsData.toastContent !== null && customCmsData.toastContent) {
    customTitle = customCmsData.toastContent.title?.replace(
      /{userName}/gi,
      userName,
    );
    const customBodyContent = customCmsData.toastContent.body?.internal?.content;
    const customBodyString = customBodyContent
      ? customBodyContent.replace(/{userName}/gi, userName)
      : null;
    customBody = customBodyString ? JSON.parse(customBodyString) : null;
  }

  const [throwConfetti, setThrowConfetti] = useState(false);
  useEffect(() => {
    setThrowConfetti(true);
  }, []);

  // Return the toast based on the type
  switch (toastType) {
    case 'celebratory':
      // Celebratory completed task toast
      return (
        <>
          <div className="flex w-full justify-between">
            <div className="mr-[16px]  flex-col">
              <div className="mb-1 flex items-center">
                <div className=" h-[25px] w-[25px] flex-shrink-0">
                  <CheckIcon className="text-neutral-black" />
                </div>

                <div className=" text-neutral-black ml-1 font-sans font-[20px] font-bold uppercase">
                  {celebratoryTitle}
                </div>
              </div>
              <div className="[&_p]:text-neutral-black [&_p]:font-serif [&_p]:text-base [&_p]:leading-[25px]">
                {celebratoryBody && documentToReactComponents(celebratoryBody, richTextOptions)}
              </div>
            </div>
            <button
              onClick={closeToast}
              className="bg-neutral-black text-neutral-white flex h-[36px] w-[36px] flex-shrink-0 items-center justify-center rounded-full"
            >
              <ToastClose />
            </button>
          </div>
          <Confetti active={throwConfetti} config={confettiConfig} />
        </>
      );
    case 'onboarding':
      // First Use Onboarding toast
      return (
        <div className="flex w-full justify-between">
          <div className="mr-[16px] flex-col">
            <div className="text-neutral-white mb-1 font-sans font-[20px] font-bold uppercase">
              {onboardingTitle}
            </div>
            <div className="[&_p]:text-neutral-white [&_p]:font-serif [&_p]:text-base [&_p]:leading-[25px]">
              {onboardingBody && documentToReactComponents(onboardingBody, richTextOptions)}
            </div>
          </div>
          <button
            onClick={closeToast}
            className=" bg-neutral-light-grey-02 text-neutral-charcoal flex h-[36px] w-[36px] flex-shrink-0 items-center justify-center rounded-full"
          >
            <ToastClose />
          </button>
        </div>
      );

    case 'custom':
      // Custom message toast
      return (
        <div className="flex w-full justify-between">
          <div className="mr-[16px]  flex-col">
            <div className="text-neutral-white mb-1 font-sans font-[20px] font-bold uppercase">
              {customTitle}
            </div>
            <div className="[&_p]:text-neutral-white [&_p]:font-serif [&_p]:text-base [&_p]:leading-[25px]">
              {customBody && documentToReactComponents(customBody, richTextOptions)}
            </div>
          </div>
          <button
            onClick={closeToast}
            className=" bg-neutral-light-grey-02 text-neutral-charcoal flex h-[36px] w-[36px] flex-shrink-0 items-center justify-center rounded-full"
          >
            <ToastClose />
          </button>
        </div>
      );
  }
};
